import React from 'react';
import {
  Alert, Box, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InvButton from '../../../components/InvButton';
import { formatCurrency } from '../../../utils';
import { hasMedicineStock } from '../../../utils/pharmacyAvailability';
import { IPD_COLORS, cardSx } from '../../../theme';

/**
 * Alternatives for a prescribed medicine that is out of stock or not in the catalogue.
 * Shared by Active Dispenses (searches by generic name) and OPD Dispenses (searches by
 * the salt extracted from the free-text ABDM name).
 */
export default function SubstitutePickerDialog({
  open,
  loading,
  items = [],
  prescribedMedicine,
  searchTerm,
  onSelect,
  onClose,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      aria-labelledby="substitute-picker-title"
      PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}>
      <DialogTitle id="substitute-picker-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Substitute medicines</Typography>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mt: 0.35 }}>
            Prescribed: {prescribedMedicine} · Search: {searchTerm}
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Close" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        {loading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: IPD_COLORS.primary }} />
          </Box>
        ) : items.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: '10px' }}>
            No substitute medicines found for &quot;{searchTerm}&quot; at this store.
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item Name</TableCell>
                <TableCell>Price</TableCell>
                <TableCell align="right">Available Qty</TableCell>
                <TableCell>Batch Number</TableCell>
                <TableCell align="right" sx={{ width: 120 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((sub) => {
                const qty = Number(sub.available_quantity ?? sub.stock) || 0;
                const inStock = hasMedicineStock(sub);
                return (
                  <TableRow key={sub.item_id || sub.item_name}>
                    <TableCell sx={{ fontSize: 13 }}>{sub.item_name || sub.name}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {(sub.mrp || sub.rate) ? formatCurrency(sub.mrp || sub.rate) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700 }}>{qty}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{sub.batch_number || '—'}</TableCell>
                    <TableCell align="right">
                      <InvButton
                        size="small"
                        variant="outlined"
                        tone="teal"
                        disabled={!inStock}
                        onClick={() => onSelect(sub)}
                      >
                        Select
                      </InvButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
